// Auto-generated from .telemetry/tracking-plan.yaml v1
// Regenerate with: product-tracking-implement-tracking skill
//
// Usage: import named functions from this module. Never call analytics.track()
// directly — always use these typed wrappers.
//
// All functions accept a TrackingContext as the last argument. Obtain it via
// the useTrackingContext() hook in components, or build it directly in non-React
// contexts.

import { analytics } from './segment'
import { mixpanel } from './mixpanel'
import { Events, MixpanelEvents, MixpanelEventName } from './events'
import {
  UserTraits,
  AccountGroupTraits,
  BrandGroupTraits,
  UserInvitedProps,
  UserAddedProps,
  InsightViewedProps,
  LabelSelectedProps,
  DateRangeSelectedProps,
  InsightSearchedProps,
  InsightDrawerOpenedProps,
  GalleryViewedProps,
  InsightsExportedProps,
  AdSummaryViewedProps,
  ViewSwitchedProps,
  KpiChangedProps,
  BrandConnectedProps,
  AdApprovedProps,
  AdDuplicatedProps,
  AdDeletedProps,
  AdDraftCreatedProps,
  CommentAddedProps,
  ShareInsightProps,
  DownloadPngProps,
  CopyImageProps,
} from './types'

// ─── Context ──────────────────────────────────────────────────────────────────

/**
 * Tracking context required by every track() call.
 * accountId is used to attribute events to the correct account group in Segment.
 * superuser must be true for internal team members — all calls are no-ops for superusers.
 */
export interface TrackingContext {
  accountId: string
  superuser: boolean
}

function isInternal(ctx: TrackingContext): boolean {
  return ctx.superuser === true
}

function asSegmentProperties<T extends object>(value: T): Record<string, unknown> {
  return value as unknown as Record<string, unknown>
}

function track(
  event: string,
  properties: Record<string, unknown>,
  ctx: TrackingContext
): void {
  if (isInternal(ctx)) return

  analytics.track(event, properties, {
    context: { groupId: ctx.accountId },
  })
}

// Internal (superuser) activity reaches Mixpanel only outside production, so the team
// can test on staging/dev. Events carry `superuser` so it can be filtered out.
const MIXPANEL_TRACKS_INTERNAL = ['staging', 'dev'].includes(process.env.REACT_APP_STAGE ?? '')

function trackMixpanel(
  event: MixpanelEventName,
  properties: Record<string, unknown>,
  ctx: TrackingContext
): void {
  if (isInternal(ctx) && !MIXPANEL_TRACKS_INTERNAL) return
  mixpanel.track(event, { ...properties, superuser: ctx.superuser })
}

// ─── Identity ─────────────────────────────────────────────────────────────────

/**
 * Call on login success, after Auth0 callback resolves.
 * Do NOT call from App.tsx or Sidebar.tsx — login only.
 */
export function identifyUser(userId: string, traits: UserTraits, superuser: boolean): void {
  if (!superuser || MIXPANEL_TRACKS_INTERNAL) {
    mixpanel.identify(userId, {
      $email: traits.email,
      $first_name: traits.first_name,
      $last_name: traits.last_name,
      account_id: traits.account_id,
      role: traits.role,
      superuser,
    })
  }
  if (superuser) return
  analytics.identify(userId, asSegmentProperties(traits))
}

/**
 * Call on logout to clear the anonymous ID.
 */
export function resetIdentity(): void {
  analytics.reset()
  mixpanel.reset()
}

// ─── Groups ───────────────────────────────────────────────────────────────────

/**
 * Call on login and whenever account traits change.
 * Also called by the daily snapshot sync (backend) to update brand_count, user_count, ad_count.
 * On login, always pass last_active_at: new Date().toISOString() to keep the account active timestamp current.
 */
export function groupAccount(traits: AccountGroupTraits, ctx: TrackingContext): void {
  if (isInternal(ctx)) return
  analytics.group(
    ctx.accountId,
    asSegmentProperties({
      ...traits,
      last_active_at: traits.last_active_at ?? new Date().toISOString(),
    })
  )
}

/**
 * Call when user navigates to a brand (context switch), on brand creation,
 * and on brand archive/unarchive.
 */
export function groupBrand(brandId: string, traits: BrandGroupTraits, ctx: TrackingContext): void {
  if (isInternal(ctx)) return
  analytics.group(brandId, asSegmentProperties(traits))
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/**
 * Call after identify() and groupAccount() on login success.
 * Login sequence: identifyUser → groupAccount → [groupBrand if brand known] → trackLogin
 */
export function trackLogin(ctx: TrackingContext): void {
  track(Events.LOGIN, {}, ctx)
}

/**
 * Call when an admin submits an invitation for a new user.
 */
export function trackUserInvited(props: UserInvitedProps, ctx: TrackingContext): void {
  track(Events.USER_INVITED, asSegmentProperties(props), ctx)
}

/**
 * Call when an invited user accepts and is added to the account in the DB.
 * Also trigger a groupAccount() call after this to increment user_count.
 */
export function trackUserAdded(props: UserAddedProps, ctx: TrackingContext): void {
  track(Events.USER_ADDED, asSegmentProperties(props), ctx)
}

// ─── Core Value — Insights ────────────────────────────────────────────────────

/**
 * Call when the Insights tab renders with data for a brand and KPI.
 * This is the primary value action — the most important event in the product.
 */
export function trackInsightViewed(props: InsightViewedProps, ctx: TrackingContext): void {
  track(Events.INSIGHT_VIEWED, asSegmentProperties(props), ctx)
}

/**
 * Call when user toggles a label type filter on or off. Fire once per label toggled.
 */
export function trackLabelSelected(props: LabelSelectedProps, ctx: TrackingContext): void {
  track(Events.LABEL_SELECTED, asSegmentProperties(props), ctx)
}

/**
 * Call when user changes the date range on either the Insights or Ad Summary tab.
 * Use the `surface` property to distinguish which tab.
 */
export function trackDateRangeSelected(props: DateRangeSelectedProps, ctx: TrackingContext): void {
  track(Events.DATE_RANGE_SELECTED, asSegmentProperties(props), ctx)
}

/**
 * Call when user adds a search query in the Insights Hub global search.
 */
export function trackInsightSearched(props: InsightSearchedProps, ctx: TrackingContext): void {
  track(Events.INSIGHT_SEARCHED, asSegmentProperties(props), ctx)
}

/**
 * Call when user clicks a label row to open the InsightDrawer.
 */
export function trackInsightDrawerOpened(props: InsightDrawerOpenedProps, ctx: TrackingContext): void {
  track(Events.INSIGHT_DRAWER_OPENED, asSegmentProperties(props), ctx)
}

/**
 * Call when user views the ad gallery on the Insights tab.
 */
export function trackGalleryViewed(props: GalleryViewedProps, ctx: TrackingContext): void {
  track(Events.GALLERY_VIEWED, asSegmentProperties(props), ctx)
}

/**
 * Call when user clicks Export to CSV on the Insights tab.
 */
export function trackInsightsExported(props: InsightsExportedProps, ctx: TrackingContext): void {
  track(Events.INSIGHTS_EXPORTED, asSegmentProperties(props), ctx)
}

/**
 * Call when user clicks "Share insight / Download PNG", from the row ⋯ menu or the drawer.
 * Sent to Mixpanel.
 */
export function trackShareInsight(props: ShareInsightProps, ctx: TrackingContext): void {
  trackMixpanel(MixpanelEvents.SHARE_INSIGHT, asSegmentProperties(props), ctx)
}

/**
 * Call when user clicks Download in the share-insight modal. Fire once per click,
 * not once per file. Sent to Mixpanel.
 */
export function trackDownloadPng(props: DownloadPngProps, ctx: TrackingContext): void {
  trackMixpanel(MixpanelEvents.DOWNLOAD_PNG, asSegmentProperties(props), ctx)
}

/**
 * Call when user clicks Copy image in the share-insight modal. Fire once per click.
 * Sent to Mixpanel.
 */
export function trackCopyImage(props: CopyImageProps, ctx: TrackingContext): void {
  trackMixpanel(MixpanelEvents.COPY_IMAGE, asSegmentProperties(props), ctx)
}

// ─── Core Value — Ad Summary ──────────────────────────────────────────────────

/**
 * Call when the Ad Summary tab renders with data for a brand and KPI.
 */
export function trackAdSummaryViewed(props: AdSummaryViewedProps, ctx: TrackingContext): void {
  track(Events.AD_SUMMARY_VIEWED, asSegmentProperties(props), ctx)
}

/**
 * Call when user toggles between visual and list view on the Ad Summary tab.
 * `view` is the view switched TO.
 */
export function trackViewSwitched(props: ViewSwitchedProps, ctx: TrackingContext): void {
  track(Events.VIEW_SWITCHED, asSegmentProperties(props), ctx)
}

// ─── Configuration ────────────────────────────────────────────────────────────

/**
 * Call when user selects a different KPI from the KPI selector.
 */
export function trackKpiChanged(props: KpiChangedProps, ctx: TrackingContext): void {
  track(Events.KPI_CHANGED, asSegmentProperties(props), ctx)
}

/**
 * Call when a brand is successfully connected to a Meta Ad Account.
 */
export function trackBrandConnected(props: BrandConnectedProps, ctx: TrackingContext): void {
  track(Events.BRAND_CONNECTED, asSegmentProperties(props), ctx)
}

// ─── Ad Management ────────────────────────────────────────────────────────────

/**
 * Call after approve/unapprove mutation succeeds.
 * is_approved: true = approved, false = unapproved.
 */
export function trackAdApproved(props: AdApprovedProps, ctx: TrackingContext): void {
  track(Events.AD_APPROVED, asSegmentProperties(props), ctx)
}

/**
 * Call after duplicate mutation succeeds. ad_id is the ORIGINAL ad's ID.
 */
export function trackAdDuplicated(props: AdDuplicatedProps, ctx: TrackingContext): void {
  track(Events.AD_DUPLICATED, asSegmentProperties(props), ctx)
}

/**
 * Call after delete mutation succeeds.
 */
export function trackAdDeleted(props: AdDeletedProps, ctx: TrackingContext): void {
  track(Events.AD_DELETED, asSegmentProperties(props), ctx)
}

/**
 * Call after new draft MetaAd is created on media upload. ad_id is the NEW draft's ID.
 */
export function trackAdDraftCreated(props: AdDraftCreatedProps, ctx: TrackingContext): void {
  track(Events.AD_DRAFT_CREATED, asSegmentProperties(props), ctx)
}

// ─── Collaboration ────────────────────────────────────────────────────────────

/**
 * Call after comment mutation succeeds.
 */
export function trackCommentAdded(props: CommentAddedProps, ctx: TrackingContext): void {
  track(Events.COMMENT_ADDED, asSegmentProperties(props), ctx)
}
